/**
 * Bill Denominations Configuration
 *
 * Denominations are loaded from the server (currency notes API).
 * Falls back to hardcoded defaults if server is unavailable.
 */

import { getCachedNotes, type CurrencyNoteData } from '../../api/currencyNoteApi';

// Fallback denominations if server notes haven't loaded
const FALLBACK_DENOMINATIONS = [
  100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000,
  100000, 200000, 500000, 1000000, 2000000, 5000000,
  10000000, 20000000, 50000000, 100000000, 500000000,
] as const;

const FALLBACK_COLORS: Record<number, number> = {
  100: 0x2E7D32, 200: 0x388E3C, 500: 0x43A047,
  1000: 0x00796B, 2000: 0x0097A7, 5000: 0x0288D1,
  10000: 0x1976D2, 20000: 0x303F9F, 50000: 0x512DA8,
  100000: 0x7B1FA2, 200000: 0xC2185B, 500000: 0xD32F2F,
  1000000: 0xE64A19, 2000000: 0xF57C00, 5000000: 0xFF6F00,
  10000000: 0xFFA000, 20000000: 0xFFB300, 50000000: 0xFFD700,
  100000000: 0xFFE082, 500000000: 0xFFF9C4,
};

/**
 * Get current denominations list.
 * Uses server notes if loaded, otherwise fallback.
 */
export function getDenominations(): number[] {
  const notes = getCachedNotes();
  if (notes.length > 0) {
    return notes.map(n => n.value);
  }
  return [...FALLBACK_DENOMINATIONS];
}

// Legacy export for compatibility — returns snapshot
export const DENOMINATIONS = FALLBACK_DENOMINATIONS;

export type Denomination = number;

/**
 * Convert HSL hue (0-360) to a hex color for material fallback.
 */
function hueToHex(hue: number, saturation = 100, brightness = 0): number {
  const s = Math.min(1, (saturation / 100) * 0.6);
  const l = Math.max(0.1, Math.min(0.9, 0.4 + brightness / 100));
  const h = hue / 360;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return (r << 16) | (g << 8) | b;
}

/**
 * Get denomination color by value.
 * Uses server note hue if available, otherwise fallback colors.
 */
export function getDenominationColor(value: number): number {
  const notes = getCachedNotes();
  if (notes.length > 0) {
    const note = notes.find(n => n.value === value);
    if (note) {
      return hueToHex(note.hue, note.saturation, note.brightness);
    }
    // Closest match
    let closest: CurrencyNoteData | null = null;
    let minDiff = Infinity;
    for (const n of notes) {
      const diff = Math.abs(n.value - value);
      if (diff < minDiff) { minDiff = diff; closest = n; }
    }
    if (closest) return hueToHex(closest.hue, closest.saturation, closest.brightness);
  }
  return FALLBACK_COLORS[value] ?? 0x888888;
}

/**
 * Format denomination for display (short compact format for UI)
 * e.g., 1000 -> "1K", 1000000 -> "1M", 1000000000 -> "1B"
 */
export function formatDenomination(value: number): string {
  if (value >= 1000000000) {
    const billions = value / 1000000000;
    return `${billions % 1 === 0 ? billions.toFixed(0) : billions.toFixed(1)}B`;
  } else if (value >= 1000000) {
    const millions = value / 1000000;
    return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  } else if (value >= 1000) {
    const thousands = value / 1000;
    return `${thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)}K`;
  }
  return `${value}`;
}

export function getNextDenominationIndex(currentIndex: number): number {
  const denoms = getDenominations();
  return currentIndex >= denoms.length - 1 ? -1 : currentIndex + 1;
}

export function getPreviousDenominationIndex(currentIndex: number): number {
  return currentIndex <= 0 ? -1 : currentIndex - 1;
}

export function cycleDenominationIndex(currentIndex: number, direction: 'up' | 'down'): number {
  const denoms = getDenominations();
  if (direction === 'up') {
    return (currentIndex + 1) % denoms.length;
  }
  return (currentIndex - 1 + denoms.length) % denoms.length;
}

export function getDenomination(index: number): number {
  const denoms = getDenominations();
  if (index < 0 || index >= denoms.length) return denoms[0];
  return denoms[index];
}

export function findClosestDenominationIndex(value: number): number {
  const denoms = getDenominations();
  let closestIndex = 0;
  let minDiff = Math.abs(denoms[0] - value);
  for (let i = 1; i < denoms.length; i++) {
    const diff = Math.abs(denoms[i] - value);
    if (diff < minDiff) { minDiff = diff; closestIndex = i; }
  }
  return closestIndex;
}
